import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';

/* ── Inline keyframe injection ── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  @keyframes carDrive {
    0%   { transform: translateX(-180px) scaleX(1); opacity:0; }
    8%   { opacity:1; }
    42%  { transform: translateX(calc(50vw - 80px)) scaleX(1); opacity:1; }
    50%  { transform: translateX(calc(50vw - 80px)) scaleX(-1); }
    92%  { transform: translateX(-180px) scaleX(-1); opacity:1; }
    100% { transform: translateX(-180px) scaleX(-1); opacity:0; }
  }
  @keyframes roadLine {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-200px); }
  }
  @keyframes floatUp {
    0%,100% { transform: translateY(0px); }
    50%      { transform: translateY(-10px); }
  }
  @keyframes fadeSlideIn {
    from { opacity:0; transform:translateY(28px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes panelReveal {
    from { opacity:0; transform:translateX(-40px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  @keyframes pulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(99,179,237,0.4); }
    50%      { box-shadow: 0 0 0 8px rgba(99,179,237,0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes headlight {
    0%,100% { opacity:0.6; }
    50%      { opacity:1; }
  }
  @keyframes starTwinkle {
    0%,100% { opacity:0.3; transform:scale(1); }
    50%      { opacity:1; transform:scale(1.4); }
  }
  @keyframes cloudDrift {
    0%   { transform:translateX(0); }
    100% { transform:translateX(-120px); }
  }
  @keyframes exhaustPuff {
    0%   { opacity:0.7; transform:translateX(0) scale(1); }
    100% { opacity:0; transform:translateX(-30px) scale(2); }
  }

  .login-root * { box-sizing:border-box; margin:0; padding:0; }

  .login-root input:-webkit-autofill,
  .login-root input:-webkit-autofill:hover,
  .login-root input:-webkit-autofill:focus {
    -webkit-text-fill-color: #e2e8f0;
    -webkit-box-shadow: 0 0 0px 1000px #1a2035 inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  .input-field {
    width:100%; padding:14px 16px 14px 48px;
    background: rgba(255,255,255,0.06);
    border: 1.5px solid rgba(255,255,255,0.1);
    border-radius:12px; color:#e2e8f0;
    font-size:14px; font-family:'DM Sans',sans-serif;
    outline:none; transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
  }
  .input-field:focus {
    border-color:#63b3ed;
    background: rgba(99,179,237,0.08);
    box-shadow: 0 0 0 3px rgba(99,179,237,0.15);
  }
  .input-field::placeholder { color:rgba(255,255,255,0.3); }

  .login-btn {
    width:100%; padding:15px;
    background: linear-gradient(135deg, #63b3ed 0%, #4299e1 50%, #3182ce 100%);
    border:none; border-radius:12px; color:#fff;
    font-size:15px; font-weight:700; font-family:'DM Sans',sans-serif;
    cursor:pointer; letter-spacing:0.5px;
    transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
    animation: pulse 2.5s ease-in-out infinite;
  }
  .login-btn:hover:not(:disabled) {
    transform:translateY(-2px);
    box-shadow: 0 12px 32px rgba(66,153,225,0.45);
    animation: none;
  }
  .login-btn:active:not(:disabled) { transform:translateY(0); }
  .login-btn:disabled { opacity:0.65; cursor:not-allowed; animation:none; }

  .captcha-img { cursor:pointer; border-radius:8px; border:1.5px solid rgba(255,255,255,0.1); transition: border-color 0.2s; }
  .captcha-img:hover { border-color:#63b3ed; }

  .refresh-btn {
    background:rgba(99,179,237,0.15); border:1.5px solid rgba(99,179,237,0.3);
    color:#63b3ed; border-radius:8px; padding:6px 10px;
    cursor:pointer; font-size:16px; transition: background 0.2s, transform 0.2s;
  }
  .refresh-btn:hover { background:rgba(99,179,237,0.25); transform:rotate(180deg); }

  .eye-btn {
    position:absolute; right:14px; top:50%; transform:translateY(-50%);
    background:none; border:none; cursor:pointer; color:rgba(255,255,255,0.4);
    font-size:16px; padding:4px; transition:color 0.2s;
  }
  .eye-btn:hover { color:#63b3ed; }

  .star { position:absolute; border-radius:50%; background:#fff; animation:starTwinkle ease-in-out infinite; }

  .road-line {
    position:absolute; height:3px; background:rgba(255,255,255,0.6);
    border-radius:2px; animation:roadLine 0.4s linear infinite;
  }
`;

/* ── SVG Car ── */
const CarSVG = () => (
  <svg width="120" height="56" viewBox="0 0 120 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Body */}
    <rect x="8" y="22" width="104" height="24" rx="6" fill="#2d5fa0"/>
    {/* Roof */}
    <path d="M28 22 L38 8 L82 8 L92 22 Z" fill="#3a7bd5"/>
    {/* Windows */}
    <rect x="40" y="10" width="16" height="11" rx="2" fill="#a8d4f5" opacity="0.85"/>
    <rect x="60" y="10" width="18" height="11" rx="2" fill="#a8d4f5" opacity="0.85"/>
    {/* Wheels */}
    <circle cx="30" cy="46" r="9" fill="#1a1a2e"/>
    <circle cx="30" cy="46" r="5" fill="#4a5568"/>
    <circle cx="30" cy="46" r="2" fill="#718096"/>
    <circle cx="90" cy="46" r="9" fill="#1a1a2e"/>
    <circle cx="90" cy="46" r="5" fill="#4a5568"/>
    <circle cx="90" cy="46" r="2" fill="#718096"/>
    {/* Headlight */}
    <ellipse cx="112" cy="32" rx="5" ry="4" fill="#fef08a" style={{animation:'headlight 1.2s ease-in-out infinite'}}/>
    <ellipse cx="114" cy="32" rx="2" ry="2.5" fill="#fde047"/>
    {/* Tail light */}
    <rect x="8" y="28" width="6" height="8" rx="2" fill="#ef4444" opacity="0.9"/>
    {/* Door line */}
    <line x1="60" y1="22" x2="60" y2="46" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
    {/* Exhaust puffs */}
    {[0,1,2].map(i=>(
      <circle key={i} cx={6-i*3} cy={38} r={3+i} fill="rgba(200,200,200,0.3)"
        style={{animation:`exhaustPuff ${0.8+i*0.2}s ease-out ${i*0.25}s infinite`}}/>
    ))}
  </svg>
);

/* ── Stars ── */
const Stars = () => {
  const stars = Array.from({length:30},(_,i)=>({
    top:`${Math.random()*60}%`, left:`${Math.random()*100}%`,
    size: Math.random()*3+1,
    delay:`${Math.random()*4}s`, dur:`${2+Math.random()*3}s`
  }));
  return (
    <>
      {stars.map((s,i)=>(
        <span key={i} className="star" style={{
          top:s.top, left:s.left, width:s.size, height:s.size,
          animationDelay:s.delay, animationDuration:s.dur
        }}/>
      ))}
    </>
  );
};

export default function Login() {
  const navigate = useNavigate();
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [captcha,     setCaptcha]     = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [captchaSvg,  setCaptchaSvg]  = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [shakeForm,   setShakeForm]   = useState(false);

  useEffect(() => { fetchCaptcha(); }, []);

  const fetchCaptcha = async () => {
    try {
      const res = await API.get('/auth/captcha', { responseType:'text' });
      setCaptchaSvg(res.data);
      setCaptcha('');
    } catch(e) { console.error(e); }
  };

  const shake = () => {
    setShakeForm(true);
    setTimeout(() => setShakeForm(false), 500);
  };

  const handleLogin = async () => {
    if (!email || !password || !captcha) {
      setError('All fields are required'); shake(); return;
    }
    setLoading(true); setError('');
    try {
      const res = await API.post('/auth/login', { loginid:email, password, captcha });
      if (res.data.status === 1) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        navigate('/dashboard');
      } else if (res.data.status === 2) {
        setError('Invalid username or password'); shake(); fetchCaptcha();
      } else if (res.data.status === 3) {
        setError('Invalid captcha — try again'); shake(); fetchCaptcha();
      } else {
        setError('Something went wrong'); shake();
      }
    } catch(e) {
      setError('Server error. Please try again.'); shake();
    }
    setLoading(false);
  };

  const handleKey = (e) => { if(e.key==='Enter') handleLogin(); };

  return (
    <>
      <style>{STYLES}</style>
      <div className="login-root" style={S.root}>

        {/* ── Night sky background ── */}
        <div style={S.skyBg}>
          <Stars/>
          {/* Moon */}
          <div style={S.moon}/>
          {/* Clouds */}
          {[10,35,65].map((left,i)=>(
            <div key={i} style={{...S.cloud, left:`${left}%`, top:`${8+i*6}%`,
              animationDelay:`${i*2}s`, animationDuration:`${12+i*4}s`}}/>
          ))}
        </div>

        {/* ── Road scene ── */}
        <div style={S.roadScene}>
          {/* Road */}
          <div style={S.road}>
            {/* Dashes */}
            {Array.from({length:12},(_,i)=>(
              <div key={i} className="road-line" style={{
                width:60, left:`${i*200}px`, top:'calc(50% - 1.5px)',
              }}/>
            ))}
          </div>
          {/* Car */}
          <div style={S.carWrap}>
            <CarSVG/>
          </div>
          {/* City silhouette */}
          <svg style={S.cityLine} viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,120 L0,80 L60,80 L60,50 L80,50 L80,30 L100,30 L100,50 L120,50 L120,60 L160,60 L160,20 L175,20 L175,0 L185,0 L185,20 L200,20 L200,60 L240,60 L240,70 L280,70 L280,40 L295,40 L295,70 L340,70 L340,55 L370,55 L370,35 L385,35 L385,55 L420,55 L420,80 L480,80 L480,45 L500,45 L500,80 L560,80 L560,60 L590,60 L590,30 L605,30 L605,10 L615,10 L615,30 L630,30 L630,60 L680,60 L680,75 L720,75 L720,50 L745,50 L745,75 L800,75 L800,55 L830,55 L830,75 L880,75 L880,40 L900,40 L900,20 L915,20 L915,40 L930,40 L930,75 L980,75 L980,60 L1020,60 L1020,75 L1060,75 L1060,50 L1080,50 L1080,75 L1140,75 L1140,55 L1165,55 L1165,35 L1180,35 L1180,55 L1220,55 L1220,80 L1280,80 L1280,45 L1300,45 L1300,80 L1380,80 L1380,90 L1440,90 L1440,120 Z"
              fill="rgba(15,20,40,0.85)"/>
          </svg>
        </div>

        {/* ── Split layout ── */}
        <div style={S.splitWrap}>

          {/* Left panel */}
          <div style={S.leftPanel}>
            <div style={{animation:'panelReveal 0.8s ease forwards'}}>
              <div style={S.brandMark}>
                <div style={S.brandIcon}>🚗</div>
                <span style={S.brandName}>Orisys</span>
              </div>
              <h1 style={S.heroTitle}>Drive the<br/><span style={S.heroAccent}>Future</span></h1>
              <p style={S.heroSub}>Premium car rental management platform. Streamline bookings, track your fleet, and grow your business.</p>
              <div style={S.featureList}>
                {['Real-time fleet tracking','Smart booking management','Advanced analytics dashboard'].map((f,i)=>(
                  <div key={i} style={{...S.feature, animationDelay:`${0.3+i*0.15}s`}}>
                    <div style={S.featureDot}/>
                    <span style={S.featureTxt}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel — Login card */}
          <div style={S.rightPanel}>
            <div style={{
              ...S.card,
              animation:'fadeSlideIn 0.7s ease 0.2s both',
              transform: shakeForm ? 'translateX(0)' : undefined,
              ...(shakeForm ? {animation:'fadeSlideIn 0.7s ease 0.2s both, shake 0.4s ease'} : {})
            }}>
              <div style={S.cardTop}>
                <h2 style={S.cardTitle}>Welcome back</h2>
                <p style={S.cardSub}>Sign in to your admin panel</p>
              </div>

              {error && (
                <div style={S.errorBox}>
                  <span style={{fontSize:14}}>⚠️</span> {error}
                </div>
              )}

              {/* Email */}
              <div style={S.fieldWrap}>
                <span style={S.fieldIcon}>✉️</span>
                <input
                  className="input-field"
                  type="email" placeholder="Email or Login ID"
                  value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={handleKey}
                />
              </div>

              {/* Password */}
              <div style={{...S.fieldWrap, position:'relative'}}>
                <span style={S.fieldIcon}>🔒</span>
                <input
                  className="input-field"
                  type={showPass?'text':'password'} placeholder="Password"
                  value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={handleKey}
                  style={{paddingRight:44}}
                />
                <button className="eye-btn" onClick={()=>setShowPass(p=>!p)}>
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>

              {/* Captcha display */}
              <div style={S.captchaRow}>
                <div
                  className="captcha-img"
                  style={S.captchaBox}
                  dangerouslySetInnerHTML={{__html:captchaSvg}}
                  onClick={fetchCaptcha}
                />
                <button className="refresh-btn" onClick={fetchCaptcha} title="Refresh captcha">↻</button>
              </div>

              {/* Captcha input */}
              <div style={S.fieldWrap}>
                <span style={S.fieldIcon}>🛡️</span>
                <input
                  className="input-field"
                  type="text" placeholder="Enter captcha text"
                  value={captcha} onChange={e=>setCaptcha(e.target.value)} onKeyDown={handleKey}
                />
              </div>

              {/* Login button */}
              <button className="login-btn" onClick={handleLogin} disabled={loading}>
                {loading
                  ? <span style={S.spinner}/>
                  : '→  Sign In to Dashboard'
                }
              </button>

              <p style={S.footNote}>Orisys Car Rental © {new Date().getFullYear()}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Styles ─── */
const S = {
  root:{
    minHeight:'100vh', width:'100%', overflow:'hidden',
    background:'#080c1a',
    fontFamily:"'DM Sans',sans-serif",
    position:'relative', display:'flex', flexDirection:'column',
  },

  /* Sky */
  skyBg:{
    position:'absolute', inset:0, zIndex:0,
    background:'linear-gradient(180deg, #080c1a 0%, #0f1729 40%, #1a2744 70%, #0d1a2e 100%)',
    overflow:'hidden',
  },
  moon:{
    position:'absolute', top:'6%', right:'12%',
    width:70, height:70, borderRadius:'50%',
    background:'radial-gradient(circle at 35% 35%, #fef9c3, #fde68a)',
    boxShadow:'0 0 40px rgba(253,230,138,0.4), 0 0 80px rgba(253,230,138,0.15)',
    animation:'floatUp 6s ease-in-out infinite',
  },
  cloud:{
    position:'absolute',
    width:120, height:40,
    background:'rgba(255,255,255,0.06)',
    borderRadius:30,
    filter:'blur(8px)',
    animation:'cloudDrift linear infinite',
  },

  /* Road scene */
  roadScene:{
    position:'absolute', bottom:0, left:0, right:0, height:180, zIndex:1, overflow:'hidden',
  },
  road:{
    position:'absolute', bottom:0, left:0, right:0, height:60,
    background:'linear-gradient(180deg,#1e2433,#141824)',
    borderTop:'3px solid rgba(255,255,255,0.15)',
    overflow:'hidden',
  },
  carWrap:{
    position:'absolute', bottom:60, left:0, zIndex:3,
    animation:'carDrive 8s ease-in-out infinite',
    filter:'drop-shadow(0 4px 12px rgba(66,153,225,0.4))',
  },
  cityLine:{
    position:'absolute', bottom:55, left:0, width:'100%', height:120, zIndex:2,
  },

  /* Layout */
  splitWrap:{
    position:'relative', zIndex:10,
    display:'flex', alignItems:'stretch', minHeight:'100vh',
  },

  /* Left */
  leftPanel:{
    flex:1, display:'flex', alignItems:'center', justifyContent:'center',
    padding:'60px 48px 200px',
  },
  brandMark:{
    display:'flex', alignItems:'center', gap:10, marginBottom:36,
  },
  brandIcon:{
    width:48, height:48, borderRadius:14,
    background:'linear-gradient(135deg,#63b3ed,#4299e1)',
    display:'flex', alignItems:'center', justifyContent:'center', fontSize:24,
    boxShadow:'0 8px 20px rgba(66,153,225,0.4)',
  },
  brandName:{
    fontSize:24, fontWeight:800, color:'#fff',
    fontFamily:"'Syne',sans-serif", letterSpacing:'-0.5px',
  },
  heroTitle:{
    fontSize:56, fontWeight:800, lineHeight:1.1, color:'#fff',
    fontFamily:"'Syne',sans-serif", letterSpacing:'-2px',
    marginBottom:20, animation:'panelReveal 0.8s ease 0.1s both',
  },
  heroAccent:{
    background:'linear-gradient(135deg,#63b3ed,#90cdf4)',
    WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
  },
  heroSub:{
    fontSize:15, color:'rgba(255,255,255,0.5)', lineHeight:1.7,
    maxWidth:360, marginBottom:32, animation:'panelReveal 0.8s ease 0.2s both',
  },
  featureList:{ display:'flex', flexDirection:'column', gap:12 },
  feature:{
    display:'flex', alignItems:'center', gap:12,
    animation:'panelReveal 0.6s ease both',
  },
  featureDot:{
    width:8, height:8, borderRadius:'50%',
    background:'linear-gradient(135deg,#63b3ed,#4299e1)',
    flexShrink:0, boxShadow:'0 0 10px rgba(99,179,237,0.6)',
  },
  featureTxt:{ fontSize:14, color:'rgba(255,255,255,0.6)', fontWeight:400 },

  /* Right / Card */
  rightPanel:{
    width:440, display:'flex', alignItems:'center', justifyContent:'center',
    padding:'60px 32px 200px',
  },
  card:{
    width:'100%', background:'rgba(255,255,255,0.05)',
    backdropFilter:'blur(20px)',
    border:'1px solid rgba(255,255,255,0.1)',
    borderRadius:24, padding:'36px 32px',
    boxShadow:'0 32px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
  },
  cardTop:{ marginBottom:24 },
  cardTitle:{
    fontSize:26, fontWeight:800, color:'#fff',
    fontFamily:"'Syne',sans-serif", letterSpacing:'-0.5px', marginBottom:4,
  },
  cardSub:{ fontSize:13, color:'rgba(255,255,255,0.4)' },

  errorBox:{
    background:'rgba(245,101,101,0.12)', border:'1px solid rgba(245,101,101,0.3)',
    borderRadius:10, padding:'10px 14px', marginBottom:16,
    color:'#fc8181', fontSize:13, display:'flex', alignItems:'center', gap:8,
  },

  fieldWrap:{ position:'relative', marginBottom:14 },
  fieldIcon:{
    position:'absolute', left:14, top:'50%', transform:'translateY(-50%)',
    fontSize:15, zIndex:1, pointerEvents:'none',
  },

  captchaRow:{
    display:'flex', alignItems:'center', gap:10, marginBottom:14,
  },
  captchaBox:{
    flex:1, minHeight:48, background:'rgba(255,255,255,0.43)',
    borderRadius:10, display:'flex', alignItems:'center',
    justifyContent:'center', overflow:'hidden', cursor:'pointer',
    padding:'4px',
  },

  spinner:{
    display:'inline-block', width:18, height:18,
    border:'2px solid rgba(255,255,255,0.3)',
    borderTopColor:'#fff', borderRadius:'50%',
    animation:'spin 0.7s linear infinite',
  },

  footNote:{
    textAlign:'center', marginTop:20, fontSize:11,
    color:'rgba(255,255,255,0.2)',
  },
};